import { useCoreStore } from '@/core/store/useCoreStore'

import { type HermesGuardFn } from './types'

/**
 * Ensures the `locationId` query param is present in brand route URLs whenever
 * an active location is set in the store. This keeps the URL accurate so that:
 * - Single-location brands always surface their location ID in the URL
 * - Multi-location brands with a selected location don't lose context when
 *   navigating via programmatic router.push calls that omit query params
 * - A page refresh restores the active location via ContextSwitcher.onMounted,
 *   which reads `locationId` from the URL to rehydrate the store
 *
 * Only applies to brand-scoped routes (those with a `brandId` route param).
 * Only injects locationId when navigating within the currently-loaded brand —
 * skipped during brand switches so the previous brand's locationId is never
 * carried into an unrelated brand's URL.
 * Runs after auth guards so the store is in a valid state.
 * @param to
 */
export const ensureLocationIdParam: HermesGuardFn = async (to) => {
  if (!to.params.brandId) return undefined

  const { activeLocationId, activeBrandId } = useCoreStore()

  // During a brand switch the store still holds the previous brand's context.
  // Let the navigation proceed unmodified; BrandView will sync the URL after
  // the new brand loads.
  if (to.params.brandId !== activeBrandId) return undefined

  if (activeLocationId && to.query.locationId !== activeLocationId) {
    return {
      hash: to.hash,
      name: to.name,
      params: to.params,
      query: { ...to.query, locationId: activeLocationId },
    }
  }

  return undefined
}
